/**
 * Voice in the browser: speech-to-text for the question (Web Speech API, the phone's own
 * recogniser — no audio leaves the device through this app), and an optional spoken answer.
 * Pure helpers here; the hook in useVoice.ts wires them to the browser.
 */
import type { Language } from './i18n.ts';

/** Recognition locale: Gulf Arabic first (the owner's market), English otherwise. */
export function recognitionLang(language: Language): string {
  return language === 'ar' ? 'ar-AE' : 'en-US';
}

export interface VoiceLike {
  readonly lang: string;
  readonly name: string;
  readonly localService: boolean;
}

/** The best voice for the language: exact region, then any Arabic/English voice; local first. */
export function pickVoice<T extends VoiceLike>(voices: readonly T[], language: Language): T | null {
  const wanted = language === 'ar' ? ['ar-AE', 'ar-SA', 'ar'] : ['en-GB', 'en-US', 'en'];
  for (const prefix of wanted) {
    const matches = voices.filter((v) => v.lang.replace('_', '-').startsWith(prefix));
    const local = matches.find((v) => v.localService);
    if (local ?? matches[0]) return local ?? matches[0] ?? null;
  }
  return null;
}

/**
 * Text shown in the box while dictating: what was typed before, then the final words, then the
 * words still being recognised.
 */
export function dictationText(before: string, finalWords: string, interim: string): string {
  return [before.trim(), finalWords.trim(), interim.trim()].filter((p) => p !== '').join(' ');
}
